import React from 'react';
import StyledToolTip from '../../../../../../../common/StyledToolTip';

type PickupMember = Record<string, any>;

const TooltipLines = ({ lines }: { lines: string[] }) => (
  <div>
    {lines.map((line, i) => (
      <div key={i}>{line}</div>
    ))}
  </div>
);

/** Display key for a member row in group tooltips (`pickupNumber` preferred, else id). */
export const getGroupMemberDisplayKey = (member: PickupMember): string =>
  `${member.pickupNumber || member.pickupId || ''}`;

export const buildGroupCreatedByTooltipLines = (members: PickupMember[]): string[] =>
  members.map((m) => `${getGroupMemberDisplayKey(m)}: ${m.createdBy || '—'}`);

export const buildGroupCreatedDateTooltipLines = (
  members: PickupMember[],
  formatDateTime: (value: unknown) => string
): string[] =>
  members.map((m) => `${getGroupMemberDisplayKey(m)}: ${formatDateTime(m.createdDateTime) || '—'}`);

export type BuildGroupPickupCreatedFieldTooltipNodesParams = {
  classes: Record<string, string>;
  showGroupedSummary: boolean;
  moreOtherPickupsCount: number;
  createdByFirstDisplay: React.ReactNode;
  dateFirstDisplay: React.ReactNode;
  createdByTooltipLines: string[];
  dateTooltipLines: string[];
};

export type GroupPickupCreatedFieldTooltipNodes = {
  createdByValueNode: React.ReactElement | null;
  createdDateValueNode: React.ReactElement | null;
};

/**
 * When the group has multiple pickups, build first-value + "+(N-1) more" nodes with per-pickup tooltip lines.
 */
export const buildGroupPickupCreatedFieldTooltipNodes = ({
  classes,
  showGroupedSummary,
  moreOtherPickupsCount,
  createdByFirstDisplay,
  dateFirstDisplay,
  createdByTooltipLines,
  dateTooltipLines
}: BuildGroupPickupCreatedFieldTooltipNodesParams): GroupPickupCreatedFieldTooltipNodes => {
  if (!showGroupedSummary) {
    return { createdByValueNode: null, createdDateValueNode: null };
  }

  const createdByValueNode =
    createdByTooltipLines.length > 0 ? (
      <StyledToolTip
        title={<TooltipLines lines={createdByTooltipLines} />}
        PopperProps={{ className: classes.groupPickupTooltipPopper }}>
        <span className={classes.groupPickupDetailValueWrap}>
          {createdByFirstDisplay}
          <span className={classes.groupPickupDetailMore}>+{moreOtherPickupsCount} more</span>
        </span>
      </StyledToolTip>
    ) : null;

  const createdDateValueNode =
    dateTooltipLines.length > 0 ? (
      <StyledToolTip
        title={<TooltipLines lines={dateTooltipLines} />}
        PopperProps={{ className: classes.groupPickupTooltipPopper }}>
        <span className={classes.groupPickupDetailValueWrap}>
          {dateFirstDisplay}
          <span className={classes.groupPickupDetailMore}>+{moreOtherPickupsCount} more</span>
        </span>
      </StyledToolTip>
    ) : null;

  return { createdByValueNode, createdDateValueNode };
};
